/**
 * Run-exit taxonomy — why an agent run ENDED, as one definition both planes
 * read.
 *
 * `excellent_worker_runs.exit_reason` is a freeform marker the runner writes
 * (`transient-api: provider overloaded or network error (cli=claude)`). It has
 * to stay freeform to survive a schema, so every surface that shows a person
 * *why* a run ended has to bucket it first. That bucketing used to live inside
 * `excellent/src/agents/fleet-analytics.ts` — a server-only module that closes
 * over `getDb()`, so no client component could import it, so the second
 * consumer forked it. The fork was not cosmetic: it split SIGTERM into its own
 * bucket where the original folds it into host-death, and the same ledger rows
 * then read "15 reaped · 1 killed" on one page and "16 host death" on another.
 *
 * So the patterns live HERE, next to `WORKER_RUN_STATUS_INTENT` (the sibling
 * concept: what a run's status MEANS), stated once as SQL `LIKE` patterns.
 * Each plane compiles them to its own form:
 *
 *   • SQL   — `runExitBucketCase()` builds the `CASE WHEN … END` expression
 *             that `fleetReliability` groups by, server-side, over the table.
 *   • TS    — `classifyRunExit()` walks the same patterns as regexes to bucket
 *             one already-loaded row, anywhere, including the browser.
 *
 * The regexes are DERIVED from the LIKE patterns rather than written beside
 * them, so the two planes cannot drift: there is one list, and a mistake in it
 * is wrong in both places at once (which is the honest failure mode) instead of
 * wrong in one (which is the silent one).
 *
 * Order is significant and matches SQL `CASE` semantics — first match wins.
 */

/** The buckets, plus the two non-failure outcomes the same expression yields. */
export type RunExitBucketKey =
  | "ok"
  | "in-flight"
  | "transient-api"
  | "wall-budget"
  | "host-death"
  | "error-exit"
  | "other";

export interface RunExitBucket {
  key: RunExitBucketKey;
  /** Title-case name for a column, chip, or summary line. */
  label: string;
  /** One clause on what it means / what to do — never a restatement of label. */
  hint: string;
  /**
   * SQL `LIKE` patterns, matched in order. `%` is the only wildcard used, and
   * only at the ends — `likeToRegExp` below understands exactly that much, and
   * `runExitPatternsAreSimple()` proves no one has since added more.
   *
   * `readonly` so the table can be declared `as const`: this list is the single
   * source both planes compile from, and nothing should be able to push a
   * pattern into it at runtime.
   */
  readonly like: readonly string[];
}

/**
 * `ok` and `in-flight` are NOT failures — they are the two ways the same CASE
 * expression can decline to name one, and they are listed here so the SQL and
 * the TS agree about them too. `FAILURE_BUCKETS` below is what a UI iterates.
 */
export const RUN_EXIT_BUCKETS: readonly RunExitBucket[] = [
  {
    key: "ok",
    label: "Completed",
    hint: "finished its work and reported back",
    like: ["ok%", "recount"],
  },
  {
    key: "transient-api",
    label: "Transient API",
    hint: "provider overloaded or network blip — retryable, not the agent's fault",
    like: ["transient-api%"],
  },
  {
    key: "wall-budget",
    label: "Wall-budget watchdog",
    hint: "killed for exceeding its time budget — runaway or stuck",
    // transport-timeout: ACP host wall kill / cancelled stop (#44560) — same
    // class of "died because a timer said so", not a provider/auth fault.
    like: ["%watchdog%", "%wall budget%", "transport-timeout%"],
  },
  {
    key: "host-death",
    label: "Host death / reaped",
    hint: "the runner process or host died mid-run",
    like: ["reaped%", "%host died%", "%SIGTERM%", "exit-code=143%"],
  },
  {
    key: "error-exit",
    label: "Error exit",
    hint: "non-zero exit code or crash before finalize",
    like: ["exit-code=1%", "%crashed%"],
  },
] as const;

/** The buckets a failure breakdown iterates — everything that is not a success
 *  and not still running. `other` is appended by the classifiers, not declared,
 *  because it is the absence of a match rather than a pattern of its own. */
export const RUN_EXIT_FAILURE_KEYS: readonly RunExitBucketKey[] = [
  "transient-api",
  "wall-budget",
  "host-death",
  "error-exit",
  "other",
] as const;

const OTHER: RunExitBucket = {
  key: "other",
  label: "Other",
  hint: "uncategorized exit reason",
  like: [],
};

const IN_FLIGHT: RunExitBucket = {
  key: "in-flight",
  label: "In flight",
  hint: "no exit reason yet — the run has not ended",
  like: [],
};

const BY_KEY = new Map<RunExitBucketKey, RunExitBucket>(
  [...RUN_EXIT_BUCKETS, OTHER, IN_FLIGHT].map((b) => [b.key, b]),
);

/** Look a bucket up by key — for a stored/serialized key coming back from SQL. */
export function runExitBucket(key: string): RunExitBucket {
  return BY_KEY.get(key as RunExitBucketKey) ?? OTHER;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * One `LIKE` pattern → the equivalent regex. Handles exactly the subset the
 * table above uses: a literal, optionally wildcarded at either end. Anchors
 * where the pattern does not wildcard, so `reaped%` stays a PREFIX match and
 * cannot fire on a reason that merely contains the word.
 *
 * Case-insensitive, because SQLite's `LIKE` is case-insensitive for ASCII by
 * default — matching the SQL plane's real behavior rather than a tidier one.
 */
export function likeToRegExp(like: string): RegExp {
  const openStart = like.startsWith("%");
  const openEnd = like.endsWith("%");
  const core = like.slice(openStart ? 1 : 0, openEnd ? -1 : undefined);
  return new RegExp(`${openStart ? "" : "^"}${escapeRegExp(core)}${openEnd ? "" : "$"}`, "i");
}

/** Guard for the lockstep test: every pattern is a plain literal with `%` only
 *  at the ends, which is the whole grammar `likeToRegExp` implements. A pattern
 *  with an interior `%` (or a `_` wildcard) would silently mean two different
 *  things in SQL and in TS. */
export function runExitPatternsAreSimple(): boolean {
  return RUN_EXIT_BUCKETS.every((b) =>
    b.like.every((p) => !p.slice(1, -1).includes("%") && !p.includes("_")),
  );
}

const COMPILED: { bucket: RunExitBucket; res: RegExp[] }[] = RUN_EXIT_BUCKETS.map((b) => ({
  bucket: b,
  res: b.like.map(likeToRegExp),
}));

/**
 * Bucket ONE `exit_reason`, in TS, exactly as the SQL would.
 *
 * `null`/`undefined` is `in-flight` — a run that has not ended has no reason
 * yet, and calling that a failure is how a live fleet reads as a broken one.
 *
 * An EMPTY STRING is deliberately not in-flight: SQL's sentinel is NULL, and
 * `'' IS NULL` is false, so the CASE walks its patterns and lands on `other`.
 * Special-casing `""` here felt tidier and made the two planes disagree — the
 * lockstep test caught it. Mirroring SQL exactly is the invariant; tidiness is
 * not.
 */
export function classifyRunExit(reason: string | null | undefined): RunExitBucket {
  if (reason == null) return IN_FLIGHT;
  for (const c of COMPILED) {
    if (c.res.some((re) => re.test(reason))) return c.bucket;
  }
  return OTHER;
}

/**
 * The SQL `CASE` expression that buckets `exit_reason` server-side, built from
 * the same list. Pass the column name if it needs qualifying (`r.exit_reason`).
 *
 * Emitted rather than hand-written so a new bucket is one entry in the table
 * above and lands in both planes at once.
 */
export function runExitBucketCase(column = "exit_reason"): string {
  const whens = RUN_EXIT_BUCKETS.map((b) => {
    const tests = b.like.map((p) => `${column} LIKE '${p.replace(/'/g, "''")}'`).join(" OR ");
    return `    WHEN ${tests} THEN '${b.key}'`;
  });
  return [
    "CASE",
    `    WHEN ${column} IS NULL THEN 'in-flight'`,
    ...whens,
    "    ELSE 'other'",
    "  END",
  ].join("\n");
}
